import {CommonModule} from '@angular/common';
import {Component, Input} from '@angular/core';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {
  FeeSnapshot,
  FeeSnapshotItem,
  FeeTier,
  FeeType,
  FlowDirection,
} from '@gatewee/types/fee';
import {PaymentMethod} from '@gatewee/types/payment';
import {UtilsModule} from '@gatewee/utils';
import {GateweeExpandChevronComponent} from '../gatewee-expand-chevron/gatewee-expand-chevron.component';
import {GateweeIconComponent} from '../gatewee-icon/gatewee-icon.component';

export interface FeeSnapshotTierRow {
  minAmount: number;
  maxAmount: number | null;
  rangeLabel: string;
  displayValue: string;
  feeType: 'rate' | 'amount' | 'mix';
}

/** One row in the captured-fees table (merged domain+recipient or standalone snapshot lines). */
export interface FeeSnapshotTableRow {
  paymentMethod: PaymentMethod | string | null | undefined;
  /** When true, show translated cash-out label instead of the payment method pipe. */
  useCashOutLabel: boolean;
  installments?: number | null;
  brand?: string | null;
  operation?: FlowDirection;
  feeType: 'rate' | 'amount' | 'mix';
  /** @deprecated Use primaryDisplayValue */
  displayValue: string;
  apiType?: FeeType;
  tiers: FeeSnapshotTierRow[];
  primaryDisplayValue: string;
  /** Human-readable primary tier band (min–max); shown in the Faixa column. */
  tierRangeLabel: string;
  hasMultipleTiers: boolean;
  tierExpandKey: string;
}

export type FeeSnapshotDisplayEntry =
  | {kind: 'row'; row: FeeSnapshotTableRow}
  | {
      kind: 'credit-card-group';
      groupKey: string;
      primary: FeeSnapshotTableRow;
      extras: FeeSnapshotTableRow[];
      hiddenCount: number;
    };

export type FeeSnapshotVisibleLine =
  | {
      type: 'row';
      entry: FeeSnapshotDisplayEntry;
      row: FeeSnapshotTableRow;
      rowIndex: number;
      isGroupPrimary?: boolean;
      groupKey?: string;
      hiddenCount?: number;
    }
  | {
      type: 'tier-panel';
      entry: FeeSnapshotDisplayEntry;
      row: FeeSnapshotTableRow;
    }
  | {
      type: 'cc-installments';
      entry: FeeSnapshotDisplayEntry;
      groupKey: string;
      rows: FeeSnapshotTableRow[];
    };

interface FeeParts {
  fixed: number | null;
  rate: number | null;
}

@Component({
  selector: 'gatewee-fee-snapshot-table',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    UtilsModule,
    GateweeIconComponent,
    GateweeExpandChevronComponent,
  ],
  templateUrl: './gatewee-fee-snapshot-table.component.html',
  styleUrls: ['./gatewee-fee-snapshot-table.component.css'],
})
export class GateweeFeeSnapshotTableComponent {
  /** Captured fee snapshot from an invoice, withdrawal, etc. */
  @Input() feeSnapshot?: FeeSnapshot | null;

  /** ISO currency for amount-type fees (display). */
  @Input() currencyCode = 'BRL';

  /** Locale for amount formatting. */
  @Input() amountLocale = 'pt-BR';

  readonly expandedCreditCardGroups = new Set<string>();
  readonly expandedTierRows = new Set<string>();

  constructor(private readonly translate: TranslateService) {}

  private static asArray(
    value: FeeSnapshotItem[] | undefined | null,
  ): FeeSnapshotItem[] {
    return Array.isArray(value) ? value : [];
  }

  get flatRows(): FeeSnapshotTableRow[] {
    return this.buildRows(this.feeSnapshot);
  }

  get displayEntries(): FeeSnapshotDisplayEntry[] {
    return this.groupCreditCardRows(this.flatRows);
  }

  get visibleLines(): FeeSnapshotVisibleLine[] {
    const lines: FeeSnapshotVisibleLine[] = [];
    let rowIndex = 0;

    for (const entry of this.displayEntries) {
      if (entry.kind === 'row') {
        rowIndex++;
        lines.push({type: 'row', entry, row: entry.row, rowIndex});
        this.appendTierPanelLine(lines, entry, entry.row);
        continue;
      }

      rowIndex++;
      lines.push({
        type: 'row',
        entry,
        row: entry.primary,
        rowIndex,
        isGroupPrimary: true,
        groupKey: entry.groupKey,
        hiddenCount: entry.hiddenCount,
      });
      this.appendTierPanelLine(lines, entry, entry.primary);

      if (this.isCreditCardGroupExpanded(entry.groupKey)) {
        lines.push({
          type: 'cc-installments',
          entry,
          groupKey: entry.groupKey,
          rows: entry.extras,
        });
      }
    }
    return lines;
  }

  get totalRecordCount(): number {
    return this.flatRows.length;
  }

  get hasRows(): boolean {
    return this.totalRecordCount > 0;
  }

  feeTypeBadgeClass(type: string): string {
    switch (type) {
      case 'rate':
        return 'gw-bg-status-info-pale gw-text-status-info';
      case 'amount':
        return 'gw-bg-status-positive-pale gw-text-status-positive';
      default:
        return 'gw-bg-status-warning-pale gw-text-status-warning';
    }
  }

  isCreditCardGroupExpanded(groupKey: string): boolean {
    return this.expandedCreditCardGroups.has(groupKey);
  }

  toggleCreditCardGroup(groupKey: string): void {
    if (this.expandedCreditCardGroups.has(groupKey)) {
      this.expandedCreditCardGroups.delete(groupKey);
    } else {
      this.expandedCreditCardGroups.add(groupKey);
    }
  }

  isTierRowExpanded(key: string): boolean {
    return this.expandedTierRows.has(key);
  }

  toggleTierRow(key: string): void {
    if (this.expandedTierRows.has(key)) {
      this.expandedTierRows.delete(key);
    } else {
      this.expandedTierRows.add(key);
    }
  }

  trackVisibleLine(_index: number, line: FeeSnapshotVisibleLine): string {
    if (line.type === 'cc-installments') {
      return `cc-panel-${line.groupKey}`;
    }
    if (line.type === 'tier-panel') {
      return `tier-panel-${line.row.tierExpandKey}`;
    }
    return [
      'row',
      line.row.tierExpandKey,
      line.groupKey ?? '',
      String(line.rowIndex),
    ].join('|');
  }

  isDefaultOpenTierRange(tier: FeeSnapshotTierRow): boolean {
    return tier.minAmount === 0 && tier.maxAmount === null;
  }

  private appendTierPanelLine(
    lines: FeeSnapshotVisibleLine[],
    entry: FeeSnapshotDisplayEntry,
    row: FeeSnapshotTableRow,
  ): void {
    if (row.hasMultipleTiers && this.isTierRowExpanded(row.tierExpandKey)) {
      lines.push({type: 'tier-panel', entry, row});
    }
  }

  private buildRows(snap?: FeeSnapshot | null): FeeSnapshotTableRow[] {
    if (!snap) {
      return [];
    }
    const domain = GateweeFeeSnapshotTableComponent.asArray(snap.domain);
    const recipient = GateweeFeeSnapshotTableComponent.asArray(snap.recipient);

    if (domain.length > 0) {
      const rows = domain.map(f => this.mergeDomainFee(f, recipient));
      const unmatchedRecipientRows = recipient
        .filter(r => !domain.some(d => this.isSameFeeTarget(d, r)))
        .map(r => this.rowFromSnapshotItem(r));
      return [...rows, ...unmatchedRecipientRows];
    }
    if (recipient.length > 0) {
      return recipient.map(f => this.rowFromSnapshotItem(f));
    }
    return [];
  }

  private groupCreditCardRows(
    rows: FeeSnapshotTableRow[],
  ): FeeSnapshotDisplayEntry[] {
    const result: FeeSnapshotDisplayEntry[] = [];
    const consumed = new Set<number>();

    for (let i = 0; i < rows.length; i++) {
      if (consumed.has(i)) {
        continue;
      }
      const row = rows[i];
      if (!this.isCreditCardRow(row)) {
        result.push({kind: 'row', row});
        continue;
      }

      const operation = row.operation ?? '';
      const ccIndices: number[] = [];
      for (let j = 0; j < rows.length; j++) {
        const candidate = rows[j];
        if (
          this.isCreditCardRow(candidate) &&
          (candidate.operation ?? '') === operation
        ) {
          ccIndices.push(j);
        }
      }
      ccIndices.forEach(idx => consumed.add(idx));

      const ccRows = ccIndices
        .map(idx => rows[idx])
        .sort((a, b) => (a.installments ?? 1) - (b.installments ?? 1));

      if (ccRows.length <= 1) {
        result.push({kind: 'row', row: ccRows[0]});
        continue;
      }

      const groupKey = `cc-${operation}`;
      result.push({
        kind: 'credit-card-group',
        groupKey,
        primary: ccRows[0],
        extras: ccRows.slice(1),
        hiddenCount: ccRows.length - 1,
      });
    }
    return result;
  }

  private isCreditCardRow(row: FeeSnapshotTableRow): boolean {
    return row.paymentMethod === PaymentMethod.CREDIT_CARD;
  }

  private rowFromSnapshotItem(fee: FeeSnapshotItem): FeeSnapshotTableRow {
    const isCashOut = fee.operation === FlowDirection.CASH_OUT;
    const tiers = this.extractTiers(fee);
    const primaryDisplayValue =
      tiers[0]?.displayValue ?? this.formatCurrency(0);
    const feeType = this.resolveRowFeeType(fee.type, tiers);
    const tierRangeLabel = this.resolvePrimaryTierRangeLabel(tiers);

    return {
      paymentMethod: fee.paymentMethod,
      useCashOutLabel: isCashOut && !fee.paymentMethod,
      installments: fee.installments,
      brand: fee.brand,
      operation: fee.operation,
      feeType,
      displayValue: primaryDisplayValue,
      apiType: fee.type,
      tiers,
      primaryDisplayValue,
      tierRangeLabel,
      hasMultipleTiers: tiers.length > 1,
      tierExpandKey: this.buildTierExpandKey(fee),
    };
  }

  private resolvePrimaryTierRangeLabel(tiers: FeeSnapshotTierRow[]): string {
    if (!tiers.length) {
      return '—';
    }
    if (tiers.length > 1) {
      return this.translate.instant('common.feeSnapshotTable.tierCount', {
        count: tiers.length,
      });
    }
    const tier = tiers[0];
    if (tier.minAmount === 0 && tier.maxAmount === null) {
      return this.translate.instant('common.feeSnapshotTable.tierAllAmounts');
    }
    return tier.rangeLabel;
  }

  private mergeDomainFee(
    fee: FeeSnapshotItem,
    recipientList: FeeSnapshotItem[],
  ): FeeSnapshotTableRow {
    const recipientPart =
      fee.operation === FlowDirection.CASH_OUT
        ? recipientList.find(r => r.operation === FlowDirection.CASH_OUT)
        : this.findRecipientMatch(
            recipientList,
            fee.paymentMethod,
            fee.installments,
            fee.brand,
            fee.operation,
          );

    const mergedFee = recipientPart
      ? this.mergeSnapshotItems(fee, recipientPart)
      : fee;

    return this.rowFromSnapshotItem(mergedFee);
  }

  private mergeSnapshotItems(
    domain: FeeSnapshotItem,
    recipient: FeeSnapshotItem,
  ): FeeSnapshotItem {
    const domainTiers = this.sortTiers(domain.tiers ?? []);
    const recipientTiers = this.sortTiers(recipient.tiers ?? []);

    if (domainTiers.length > 0 || recipientTiers.length > 0) {
      return {
        ...domain,
        type: this.inferMergedApiType(domain.type, recipient.type),
        tiers: this.mergeTierBands(domainTiers, recipientTiers),
      };
    }

    const mergedParts = this.mergeFeeParts(
      this.extractLegacyFeeParts(domain),
      this.extractLegacyFeeParts(recipient),
    );

    return {
      ...domain,
      type: this.inferMergedApiType(domain.type, recipient.type),
      fixedAmount: mergedParts.fixed ?? undefined,
      rateValue: mergedParts.rate ?? undefined,
      value:
        mergedParts.fixed !== null
          ? mergedParts.fixed
          : mergedParts.rate !== null
          ? mergedParts.rate
          : domain.value,
    };
  }

  private mergeTierBands(
    domainTiers: FeeTier[],
    recipientTiers: FeeTier[],
  ): FeeTier[] {
    const map = new Map<string, FeeTier>();
    const key = (t: FeeTier) => `${t.minAmount}|${t.maxAmount ?? 'null'}`;

    for (const tier of domainTiers) {
      map.set(key(tier), {...tier});
    }
    for (const tier of recipientTiers) {
      const k = key(tier);
      const existing = map.get(k);
      if (existing) {
        map.set(k, {
          ...existing,
          fixedAmount: (existing.fixedAmount ?? 0) + (tier.fixedAmount ?? 0),
          rateValue: (existing.rateValue ?? 0) + (tier.rateValue ?? 0),
        });
      } else {
        map.set(k, {...tier});
      }
    }

    return Array.from(map.values()).sort((a, b) => a.minAmount - b.minAmount);
  }

  private inferMergedApiType(
    domainType: FeeType,
    recipientType: FeeType,
  ): FeeType {
    if (domainType === 'compound' || recipientType === 'compound') {
      return 'compound';
    }
    if (domainType !== recipientType) {
      return 'compound';
    }
    return domainType;
  }

  private isSameFeeTarget(a: FeeSnapshotItem, b: FeeSnapshotItem): boolean {
    return (
      (a.operation ?? null) === (b.operation ?? null) &&
      (a.paymentMethod ?? null) === (b.paymentMethod ?? null) &&
      (a.installments ?? null) === (b.installments ?? null) &&
      (a.brand ?? null) === (b.brand ?? null)
    );
  }

  private findRecipientMatch(
    list: FeeSnapshotItem[],
    method: PaymentMethod | string | undefined | null,
    installments: number | null | undefined,
    brand?: string | null,
    operation?: FlowDirection,
  ): FeeSnapshotItem | undefined {
    if (!list.length || method === undefined || method === null) {
      return undefined;
    }
    const inst = installments ?? 0;
    return list.find(
      e =>
        e.paymentMethod === method &&
        (e.installments ?? 0) === inst &&
        (e.brand ?? null) === (brand ?? null) &&
        (e.operation ?? null) === (operation ?? null),
    );
  }

  private extractTiers(fee: FeeSnapshotItem): FeeSnapshotTierRow[] {
    const sorted = this.sortTiers(fee.tiers ?? []);
    if (sorted.length > 0) {
      return sorted.map(tier => this.tierToDisplayRow(tier));
    }
    const legacy = this.extractLegacyFeeParts(fee);
    return [
      {
        minAmount: 0,
        maxAmount: null,
        rangeLabel: this.formatTierRange(0, null),
        displayValue: this.composeDisplayValue(legacy),
        feeType: this.resolvePartsFeeType(legacy),
      },
    ];
  }

  private tierToDisplayRow(tier: FeeTier): FeeSnapshotTierRow {
    const parts = this.partsFromTier(tier);
    return {
      minAmount: tier.minAmount,
      maxAmount: tier.maxAmount,
      rangeLabel: this.formatTierRange(tier.minAmount, tier.maxAmount),
      displayValue: this.composeDisplayValue(parts),
      feeType: this.resolvePartsFeeType(parts),
    };
  }

  private partsFromTier(tier: FeeTier): FeeParts {
    return {
      fixed: this.toMeaningfulNumber(tier.fixedAmount),
      rate: this.toMeaningfulNumber(tier.rateValue),
    };
  }

  private extractLegacyFeeParts(fee: FeeSnapshotItem): FeeParts {
    const fixedAlias =
      fee.fixedAmount ??
      fee.fixedValue ??
      (fee.type === 'amount' ? fee.value : null);
    const rateAlias =
      fee.rateValue ??
      fee.fixedRate ??
      (fee.type === 'rate' ? fee.value : null);

    return {
      fixed: this.toMeaningfulNumber(fixedAlias),
      rate: this.toMeaningfulNumber(rateAlias),
    };
  }

  private sortTiers(tiers: FeeTier[]): FeeTier[] {
    return [...tiers].sort((a, b) => a.minAmount - b.minAmount);
  }

  private mergeFeeParts(
    domain: FeeParts,
    recipient: FeeParts | null,
  ): FeeParts {
    if (!recipient) {
      return domain;
    }
    return {
      fixed: this.sumNullable(domain.fixed, recipient.fixed),
      rate: this.sumNullable(domain.rate, recipient.rate),
    };
  }

  private sumNullable(a: number | null, b: number | null): number | null {
    if (a === null && b === null) {
      return null;
    }
    return (a ?? 0) + (b ?? 0);
  }

  private resolveRowFeeType(
    apiType: FeeType | undefined,
    tiers: FeeSnapshotTierRow[],
  ): 'rate' | 'amount' | 'mix' {
    if (apiType === 'compound') {
      return 'mix';
    }
    if (tiers.length === 1) {
      return tiers[0].feeType;
    }
    const hasMix = tiers.some(t => t.feeType === 'mix');
    if (hasMix) {
      return 'mix';
    }
    const hasRate = tiers.some(t => t.feeType === 'rate');
    const hasAmount = tiers.some(t => t.feeType === 'amount');
    if (hasRate && hasAmount) {
      return 'mix';
    }
    if (hasRate) {
      return 'rate';
    }
    return 'amount';
  }

  private resolvePartsFeeType(parts: FeeParts): 'rate' | 'amount' | 'mix' {
    const hasFixed = parts.fixed !== null;
    const hasRate = parts.rate !== null;
    if (hasFixed && hasRate) {
      return 'mix';
    }
    if (hasRate) {
      return 'rate';
    }
    return 'amount';
  }

  private composeDisplayValue(parts: FeeParts): string {
    const chunks: string[] = [];
    if (parts.fixed !== null) {
      chunks.push(this.formatCurrency(parts.fixed));
    }
    if (parts.rate !== null) {
      chunks.push(this.formatRate(parts.rate));
    }
    return chunks.length ? chunks.join(' + ') : this.formatCurrency(0);
  }

  private formatTierRange(min: number, max: number | null): string {
    const minLabel = this.formatCurrency(min);
    if (max === null || max === undefined) {
      const unlimited = this.translate.instant(
        'common.feeSnapshotTable.tierUnlimited',
      );
      return this.translate.instant('common.feeSnapshotTable.tierRange', {
        min: minLabel,
        max: unlimited,
      });
    }
    return this.translate.instant('common.feeSnapshotTable.tierRange', {
      min: minLabel,
      max: this.formatCurrency(max),
    });
  }

  private buildTierExpandKey(fee: FeeSnapshotItem): string {
    return [
      fee.operation ?? '',
      fee.paymentMethod ?? '',
      fee.installments ?? '',
      fee.brand ?? '',
    ].join('|');
  }

  private formatRate(value: number): string {
    return `${this.toSafeNumber(value).toString().replace('.', ',')}%`;
  }

  private formatCurrency(value: number): string {
    const num = this.toSafeNumber(value);
    try {
      return new Intl.NumberFormat(this.amountLocale, {
        style: 'currency',
        currency: this.currencyCode,
      }).format(num);
    } catch {
      return `R$ ${num.toFixed(2)}`.replace('.', ',');
    }
  }

  private toMeaningfulNumber(value: unknown): number | null {
    const parsed = this.toNumberOrNull(value);
    if (parsed === null || parsed === 0) {
      return null;
    }
    return parsed;
  }

  private toSafeNumber(value: unknown): number {
    const parsed = this.toNumberOrNull(value);
    return parsed ?? 0;
  }

  private toNumberOrNull(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }
    const parsed = Number(String(value).replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : null;
  }
}
